import { NgIf, NgClass, NgFor, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ChartComponent } from '@next/core-lib/chart';
import { SummaryCardComponent } from '@next/core-lib/components';
import { AgrNumberPipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SeriesOptionsType, YAxisOptions } from 'highcharts';

import { SummaryCardKPIChartItem } from './summary-card-kpi-chart-item';

@Component({
  selector: 'app-summary-card-kpi-chart',
  templateUrl: './summary-card-kpi-chart.component.html',
  styleUrls: ['./summary-card-kpi-chart.component.scss'],
  imports: [
    SummaryCardComponent,
    NgIf,
    NgClass,
    MatIcon,
    NgFor,
    NgTemplateOutlet,
    MatButton,
    ChartComponent,
    TranslatePipe,
  ],
})
export class SummaryCardKPIChartComponent extends SummaryCardComponent implements OnChanges {
  private readonly decimalPipe = inject(AgrNumberPipe);
  private readonly locale = inject(LocaleService);

  @Input() items: SummaryCardKPIChartItem[];
  @Input() summaryLabel: string;
  @Input() summaryValue: number;
  @Input() summaryUnit?: string;
  @Input() prefixSummaryUnit?: boolean;
  @Input() summaryValueDecimals?: number;
  @Input() benchmarkAgendaLabel?: string;
  @Input() benchmarkValue?: number;
  @Input() benchmarkUnit?: string;
  @Input() prefixBenchmarkUnit?: boolean;
  @Input() benchmarkValueDecimals?: number;
  @Input() showBenchmarkDeviationPercentage?: boolean;
  @Input() isLowerThanBenchmarkBetter?: boolean;
  @Input() showBenchmarkDeviationColor?: boolean;

  displayedItems: SummaryCardKPIChartItem[];
  collapsedNumberOfItems = 4;
  isCollapsed: boolean;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.isCollapsed = true;
  }

  charts: { [key: string]: SeriesOptionsType[] } = {};
  options: { [key: string]: Highcharts.Options } = {};

  ngOnChanges() {
    this.createCharts();
    this.isCollapsed = true;
  }

  getKpiItemValue(item: SummaryCardKPIChartItem) {
    if (item.value === undefined || this.isNAN(item.value)) {
      return this.locale.translate('n/a');
    }

    return item.numberOfDecimals !== undefined
      ? this.decimalPipe.transform(item.value, `1.0-${item.numberOfDecimals}`)
      : this.decimalPipe.transform(item.value);
  }

  getSummaryValue() {
    if (this.summaryValue === undefined || this.isNAN(this.summaryValue)) {
      return this.locale.translate('n/a');
    }
    return this.summaryValueDecimals !== undefined
      ? this.decimalPipe.transform(this.summaryValue, `1.0-${this.summaryValueDecimals}`)
      : this.decimalPipe.transform(this.summaryValue);
  }

  getBenchmarkValue() {
    if (this.benchmarkValue === undefined || this.isNAN(this.benchmarkValue)) {
      return this.locale.translate('n/a');
    }
    return this.benchmarkValueDecimals !== undefined
      ? this.decimalPipe.transform(this.benchmarkValue, `1.0-${this.benchmarkValueDecimals}`)
      : this.decimalPipe.transform(this.benchmarkValue);
  }

  getBenchmarkDeviationPercentageLabel() {
    const percentage = this.getBenchmarkDeviationPercentage();
    if (percentage !== undefined && !this.isNAN(percentage)) {
      let label = percentage > 0 ? '+' : '';
      label += this.decimalPipe.transform(percentage, '1.0-1') + '%';
      return label;
    }
    return '';
  }

  getBenchmarkDeviationPercentage(): number {
    if (
      this.benchmarkValue !== undefined &&
      !this.isNAN(this.benchmarkValue) &&
      this.summaryValue !== undefined &&
      !this.isNAN(this.summaryValue)
    ) {
      return ((this.summaryValue - this.benchmarkValue) / this.benchmarkValue) * 100.0;
    }
    return undefined;
  }

  getEmptyValue() {
    return this.locale.translate('n/a');
  }

  createCharts(): void {
    this.charts = {};
    if (this.items !== undefined) {
      // Set min/max for the charts.

      // Process charts.
      this.items.forEach((item) => {
        this.charts[item.label] = [
          {
            type: 'bar',
            name: item.label,
            color: '#29639d',
            borderColor: 'transparent',
            data: [item.value],
          },
        ];

        this.options[item.label] = this.getDefaultOptions(item);

        if (item.min !== undefined && item.min !== null) {
          (this.options[item.label].yAxis as YAxisOptions).min = item.min;
        }

        if (item.max !== undefined && item.max !== null) {
          (this.options[item.label].yAxis as YAxisOptions).max = item.max;
        }

        if (item.threshold !== undefined && item.threshold !== null) {
          (this.options[item.label].yAxis as YAxisOptions).plotLines = [
            {
              value: item.threshold,
              width: 2,
              color: '#000',
              zIndex: 500,
            },
          ];
        }
      });
      this.displayedItems = [...this.items.slice(0, this.collapsedNumberOfItems)];
    }
  }

  showItems(indicator: string) {
    if (indicator === 'more' && this.displayedItems.length < this.items.length) {
      this.displayedItems = [...this.items];
    } else if (indicator === 'less' && this.displayedItems.length === this.items.length) {
      this.displayedItems = [...this.items.slice(0, this.collapsedNumberOfItems)];
    }
    this.isCollapsed = !this.isCollapsed;
  }

  isNAN(value: number): boolean {
    if (value === undefined) {
      return false;
    }
    return (
      Number.isNaN(value) ||
      value.toString() === 'NaN' ||
      value === Infinity ||
      !Number.isFinite(value) ||
      value.toString() === 'Infinity'
    );
  }

  getDefaultOptions(kpiItem: SummaryCardKPIChartItem): Highcharts.Options {
    const component = this;
    return {
      chart: {
        height: '13px',
        margin: 0,
        marginLeft: 1,
        plotBackgroundColor: '#efefef',
        className: 'kpiChart',
      },
      xAxis: {
        visible: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          pointPadding: 0,
        },
      },
      yAxis: {
        maxPadding: 0,
        minPadding: 0,
        title: null,
        margin: 0,
        labels: {
          enabled: false,
        },
        lineWidth: 0,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        // prettier-ignore

        formatter: function() {
          const formattedValue =
            kpiItem.numberOfDecimals !== undefined
              ? component.decimalPipe.transform(this.y, `1.0-${kpiItem.numberOfDecimals}`)
              : component.decimalPipe.transform(this.y);
          if (kpiItem.unit) {
            const translatedUnit = component.locale.translate(kpiItem.unit);
            if (kpiItem.prefixUnit === true) {
              return `${translatedUnit} ${formattedValue}`;
            } else {
              return `${formattedValue} ${translatedUnit}`;
            }
          }
          return formattedValue;
        },
        outside: true,
        enabled: true,
        useHTML: true,
        shared: true,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
    } as Highcharts.Options;
  }
}
